import React, { useState, useEffect, useRef } from 'react';
import { Building2, ChevronDown, Check, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface UserAccount {
  accountId: string;
  companyName: string;
  logo?: string;
  role: string;
  permissions: string[];
  joinedAt: Date;
  isCurrent: boolean;
}

export const AccountSwitcher: React.FC = () => {
  const { getUserAccounts, switchAccount } = useAuth();
  const [accounts, setAccounts] = useState<UserAccount[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [switchingToAccountId, setSwitchingToAccountId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentAccount = accounts.find(a => a.isCurrent);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const userAccounts = await getUserAccounts();
        setAccounts(userAccounts);
      } catch (error) {
        console.error('Failed to load accounts:', error);
      }
    };

    fetchAccounts();
  }, [getUserAccounts]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close dropdown if we're loading
      if (isLoading) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLoading]);

  const handleSwitchAccount = async (accountId: string) => {
    if (accountId === currentAccount?.accountId) {
      setIsOpen(false);
      return;
    }

    // Set loading state immediately for instant feedback
    setIsLoading(true);
    setSwitchingToAccountId(accountId);
    // Keep dropdown open to show loading state
    
    try {
      await switchAccount(accountId);
      // Note: switchAccount will reload the page, so we don't need to reset state
    } catch (error) {
      console.error('Failed to switch account:', error);
      setIsLoading(false);
      setSwitchingToAccountId(null);
      // Optionally show error toast here
    }
  };

  if (accounts.length <= 1) {
    // Don't show switcher if user only has one account
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !isLoading && setIsOpen(!isOpen)}
        className="w-full flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 text-primary-600 flex-shrink-0 animate-spin" />
        ) : (
          <Building2 className="w-4 h-4 text-primary-600 flex-shrink-0" />
        )}
        <span className="flex-1 text-left truncate">
          {isLoading && switchingToAccountId
            ? accounts.find(a => a.accountId === switchingToAccountId)?.companyName || 'Switching...'
            : currentAccount?.companyName || 'Select Account'}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-50 max-h-64 overflow-y-auto">
          {accounts.map((account) => {
            const isSwitching = isLoading && switchingToAccountId === account.accountId;
            const isDisabled = isLoading && !isSwitching;
            
            return (
              <button
                key={account.accountId}
                onClick={() => !isLoading && handleSwitchAccount(account.accountId)}
                disabled={isDisabled}
                className={`w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 transition-colors ${
                  isSwitching
                    ? 'bg-primary-50 dark:bg-primary-900/30 cursor-wait'
                    : isDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {isSwitching ? (
                  <Loader2 className="w-5 h-5 text-primary-600 flex-shrink-0 animate-spin" />
                ) : account.logo ? (
                  <img
                    src={account.logo}
                    alt={account.companyName}
                    className="w-5 h-5 rounded flex-shrink-0"
                  />
                ) : (
                  <Building2 className="w-5 h-5 text-primary-600 flex-shrink-0" />
                )}
                <span className="flex-1 text-left truncate">{account.companyName}</span>
                {account.isCurrent && !isSwitching && (
                  <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
                )}
                {isSwitching && (
                  <span className="text-xs text-primary-600 flex-shrink-0">Switching...</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

